import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Link } from 'react-router-dom'
import { t } from '@/lib/ui'

// ============================================================================
// HonestyPopover — the click-to-reveal primitive shared by every honesty badge
// and automation dot (SIM-015; DESIGN-BRIEF §4).
//
// Accessibility: the trigger is a real <button> with aria-expanded/haspopup;
// the panel closes on Escape (returning focus to the trigger) and on outside
// click. No shadow — the panel is separated from the page by a hairline only
// (§4). Positioned below-left of the trigger, clamped to the viewport width.
// ============================================================================

interface HonestyPopoverProps {
  /** Inner content of the trigger chip/dot (the always-visible mark). */
  triggerLabel: ReactNode
  /** Accessible name for the trigger button. */
  triggerAriaLabel: string
  /** Tailwind classes that style the trigger as a chip or dot. */
  triggerClassName: string
  /** Panel content — usually a <BadgePanel>. */
  children: ReactNode
}

export function HonestyPopover({
  triggerLabel,
  triggerAriaLabel,
  triggerClassName,
  children,
}: HonestyPopoverProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLSpanElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()

  useEffect(() => {
    if (!open) return

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    function onPointer(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  return (
    <span ref={wrapRef} className="relative inline-flex align-baseline">
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerAriaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
        className={triggerClassName}
      >
        {triggerLabel}
      </button>
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label={triggerAriaLabel}
          className="absolute left-0 top-full z-40 mt-1.5 w-max max-w-[min(20rem,calc(100vw-2rem))] cursor-default rounded border border-hairline bg-paper p-3 text-left text-sm font-normal not-italic leading-snug text-ink"
        >
          {children}
        </div>
      )}
    </span>
  )
}

// ----------------------------------------------------------------------------
// BadgePanel — the consistent inside of every honesty popover: a name, an
// explanation, optional labelled rows (statute, borrowed pattern, register id),
// and the "konepellin alle" deep link to the underlying spec.
// ----------------------------------------------------------------------------

export interface PanelRow {
  label: string
  value: ReactNode
}

interface BadgePanelProps {
  name: string
  hint: string
  rows?: PanelRow[]
  /** Spec id (e.g. "rul-032") — deep-links to /spec/:id (the everything-is-specs proof). */
  specId?: string
}

export function BadgePanel({ name, hint, rows, specId }: BadgePanelProps) {
  return (
    <div className="space-y-1.5">
      <p className="font-semibold text-ink">{name}</p>
      <p className="text-muted">{hint}</p>
      {rows && rows.length > 0 && (
        <dl className="space-y-1 border-t border-hairline pt-1.5">
          {rows.map((row) => (
            <div key={row.label} className="flex gap-1.5">
              <dt className="shrink-0 font-medium text-muted">{row.label}:</dt>
              <dd className="text-ink">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {specId && (
        <p className="border-t border-hairline pt-1.5">
          <Link
            to={`/spec/${specId}`}
            className="font-medium text-accent underline decoration-hairline underline-offset-2 hover:decoration-accent"
          >
            {t('konepellinAlle.label')} → {specId}
          </Link>
        </p>
      )}
    </div>
  )
}
